import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import DraggableModal from "../DraggableModal";
import EmailDialog from "../EmailDialog";
import { htmlToPdfBlob, safeFilename } from "../../pdfRenderer";
import { escHtml, reportStyles, policyFooter, openPreviewWindow, downloadBlob } from "./auditCommon";
import { loadFromSupabase } from "../../supabaseDB";
import { LOGO_B64 } from "../SalesReports";
import { appendDisclosureByAccount } from "../emailHelpers";

/* ----------------------------------------------------------------------------
 * Customer Complaint Procedure — content per the organic consultant's template
 * (Programa de Recepción y Manejo de Quejas). Bilingual ES/EN, language picker
 * at print/email time, editable effective date (default = audit start − 5 days),
 * Ground-Fresh institutional logo in the header, and the complaint-record form
 * as Exhibit A / Anexo A.
 * -------------------------------------------------------------------------- */

const T = {
  en: {
    title: "Complaint Reception & Handling Program",
    close: "Close", preview: "Preview", pdf: "Download PDF", email: "Email", busy: "Working…", sending: "Sending…",
    langLabel: "Document language", effDateLabel: "Effective date", revLabel: "Revision",
    docHeaderProgram: "COMPLAINT RECEPTION & HANDLING PROGRAM",
    product: "Product", productVal: "Various", effFrom: "Effective from", revision: "Revision", areaLabel: "Scope of application", areaVal: "Distributor (Comercializadora)",
    secProcTitle: "COMPLAINT RECEPTION & HANDLING PROCEDURE",
    secPurpose: "PURPOSE", secScope: "SCOPE", secResp: "RESPONSIBILITIES", secDef: "DEFINITIONS",
    secProc: "PROCEDURE", secRecords: "RECORDS", secAnnex: "EXHIBIT",
    pageOf: "Page", pageOfMid: "of",
    purpose: "Establish the procedure for the reception, analysis, follow-up and closure of complaints related to the products marketed by Ground Fresh International Inc., ensuring timely and effective attention that maintains customer satisfaction and conformity with quality and food-safety requirements.",
    scope: "This procedure applies to all complaints received by Ground Fresh International Inc. related to quality, food safety, traceability, labeling, logistics and/or customer service, coming from clients, authorities or final consumers.",
    respQ: "Quality / Food-Safety Officer:",
    respQ1: "Receive and record complaints.",
    respQ2: "Coordinate the investigation and determine root causes.",
    respQ3: "Propose and implement corrective and preventive actions.",
    respQ4: "Follow up and close the complaint.",
    respG: "General Management:",
    respG1: "Approve the proposed corrective and preventive actions.",
    respG2: "Evaluate the effectiveness of the implemented measures.",
    respA: "Administrative / Logistics Staff:",
    respA1: "Provide the information and records required for the investigation.",
    defComplaint: "Complaint: Any expression of dissatisfaction about a marketed product or service, received from a client, authority or consumer.",
    defCorrective: "Corrective Action: Measure to eliminate the cause of a detected nonconformity.",
    defPreventive: "Preventive Action: Measure to eliminate the cause of a potential nonconformity.",
    defInvestigation: "Investigation: Process to determine the causes that originated the complaint.",
    defRecall: "Product recall: Recovery of a product from all points of sale in the market, including, if necessary, down to the consumer, because it contains a defect that constitutes a health risk or a legal nonconformity.",
    proc1Title: "Reception and recording of the complaint",
    proc1a: "Every complaint received (by email, phone call, web form or in person) must be communicated immediately to the Quality/Food-Safety Officer.",
    proc1b: "Record the complaint in the Complaint Record Form within a maximum of 24 hours.",
    proc1c: "Assign a consecutive folio number for control and follow-up.",
    proc2Title: "Initial assessment",
    proc2a: "Determine the nature of the complaint: organic integrity of the product; product quality (defects, damage, appearance, taste, etc.); food safety (suspected contamination, allergens, etc.); logistics (delays, transport damage, incorrect labeling, etc.); customer service.",
    proc2b: "Assess the severity and determine whether it is necessary to: notify the competent authorities; perform a product recall or reverse traceability.",
    proc3Title: "Investigation of the complaint",
    proc3a: "Collect all related information (lot number, dates, traceability records, invoices, organic certificates, etc.).",
    proc3b: "Request additional information from the client or supplier if necessary.",
    proc3c: "Identify the root cause of the nonconformity.",
    proc4Title: "Response to the client",
    proc4a: "Send acknowledgement of receipt of the complaint to the client within no more than 48 hours.",
    proc4b: "Provide a formal response with the results of the investigation and the actions taken within a maximum of 7 business days.",
    proc5Title: "Implementation of actions",
    proc5a: "Implement corrective actions to eliminate the identified causes.",
    proc5b: "Assess and establish preventive actions to avoid recurrence.",
    proc5c: "Record all actions taken in the Complaint Record Form.",
    proc6Title: "Closure and verification",
    proc6a: "Verify the effectiveness of the corrective actions before closing the complaint.",
    proc6b: "File all records related to the complaint in the corresponding file.",
    rec1: "Complaint Record Form", rec2: "Investigation reports", rec3: "Evidence of corrective and preventive actions",
    anx1: "Complaint Record Form", anx2: "Corrective and preventive action form", anx3: "Traceability procedure",
    exhibitTitle: "EXHIBIT A — PRODUCT COMPLAINT RECORD",
    exDateTime: "Complaint date/time", exCode: "Code", exBy: "Complaint submitted by",
    exProduct: "Product description", exQty: "Quantity of product involved", exNature: "Nature of the complaint",
    exMgmt: "Management notified", exFindings: "Investigation findings",
    exActions: "Actions to address and correct the complaint and prevent recurrence",
    exResponse: "Complaint response date", exSign: "Signature", exDate: "Date",
    emailSubject: "Complaint Reception & Handling Program",
    emailBody: "Attached is the Complaint Reception & Handling Program for audit ",
  },
  es: {
    title: "Programa de Recepción y Manejo de Quejas",
    close: "Cerrar", preview: "Vista previa", pdf: "Descargar PDF", email: "Correo", busy: "Trabajando…", sending: "Enviando…",
    langLabel: "Idioma del documento", effDateLabel: "Vigencia a partir de", revLabel: "Revisión",
    docHeaderProgram: "PROGRAMA DE RECEPCIÓN Y MANEJO DE QUEJAS",
    product: "Producto", productVal: "Varios", effFrom: "Vigencia a partir de", revision: "Revisión", areaLabel: "Área de aplicación", areaVal: "Comercializadora",
    secProcTitle: "PROCEDIMIENTO DE RECEPCIÓN Y MANEJO DE QUEJAS",
    secPurpose: "PROPÓSITO", secScope: "ALCANCE", secResp: "RESPONSABILIDADES", secDef: "DEFINICIONES",
    secProc: "PROCEDIMIENTO", secRecords: "REGISTROS", secAnnex: "EXHIBIT",
    pageOf: "Página", pageOfMid: "de",
    purpose: "Establecer el procedimiento para la recepción, análisis, seguimiento y cierre de quejas relacionadas con los productos comercializados por Ground Fresh Internacional Inc., asegurando una atención oportuna y eficaz que permita mantener la satisfacción del cliente y la conformidad con los requisitos de calidad e inocuidad.",
    scope: "Este procedimiento aplica a todas las quejas recibidas por Ground Fresh Internacional Inc. relacionadas con la calidad, inocuidad, trazabilidad, etiquetado, logística y/o servicio al cliente, provenientes de clientes, autoridades o consumidores finales.",
    respQ: "Responsable de Calidad/Inocuidad:",
    respQ1: "Recibir y registrar las quejas.",
    respQ2: "Coordinar la investigación y determinar las causas raíz.",
    respQ3: "Proponer e implementar acciones correctivas y preventivas.",
    respQ4: "Dar seguimiento y cerrar la queja.",
    respG: "Gerencia General:",
    respG1: "Aprobar las acciones correctivas y preventivas propuestas.",
    respG2: "Evaluar la efectividad de las medidas implementadas.",
    respA: "Personal Administrativo/Logístico:",
    respA1: "Proporcionar información y registros requeridos para la investigación.",
    defComplaint: "Queja: Cualquier expresión de insatisfacción sobre un producto o servicio comercializado, recibida por parte de un cliente, autoridad o consumidor.",
    defCorrective: "Acción Correctiva: Medida para eliminar la causa de una no conformidad detectada.",
    defPreventive: "Acción Preventiva: Medida para eliminar la causa de una posible no conformidad.",
    defInvestigation: "Investigación: Proceso para determinar las causas que originaron la queja.",
    defRecall: "Retiro de producto: Es la recuperación de un producto de todos los puntos de venta del mercado incluyendo, caso de ser necesario, hasta el consumidor, por contener un defecto que constituya un riesgo para la salud o una inconformidad legal.",
    proc1Title: "Recepción y registro de la queja",
    proc1a: "Toda queja recibida (por correo electrónico, llamada telefónica, formulario web o presencial) deberá ser comunicada de inmediato al Responsable de Calidad/Inocuidad.",
    proc1b: "Registrar la queja en el Formato de Registro de Quejas en un plazo máximo de 24 horas.",
    proc1c: "Asignar un número de folio consecutivo para control y seguimiento.",
    proc2Title: "Evaluación inicial",
    proc2a: "Determinar la naturaleza de la queja: integridad orgánica del producto; calidad del producto (defectos, daños, apariencia, sabor, etc.); inocuidad (sospecha de contaminación, alérgenos, etc.); logística (retrasos, daños en transporte, etiquetado incorrecto, etc.); servicio al cliente.",
    proc2b: "Evaluar la gravedad y determinar si se requiere: notificar a las autoridades competentes; retiro de producto o trazabilidad inversa.",
    proc3Title: "Investigación de la queja",
    proc3a: "Recolectar toda la información relacionada (número de lote, fechas, registros de trazabilidad, facturas, certificados orgánicos, etc.).",
    proc3b: "Solicitar información adicional al cliente o proveedor si es necesario.",
    proc3c: "Identificar la causa raíz de la no conformidad.",
    proc4Title: "Respuesta al cliente",
    proc4a: "Enviar acuse de recibo de la queja al cliente en un plazo no mayor a 48 horas.",
    proc4b: "Proporcionar una respuesta formal con los resultados de la investigación y las acciones tomadas en un máximo de 7 días hábiles.",
    proc5Title: "Implementación de acciones",
    proc5a: "Implementar acciones correctivas para eliminar las causas identificadas.",
    proc5b: "Evaluar y establecer acciones preventivas para evitar la recurrencia.",
    proc5c: "Registrar en el Formato de Registro de Quejas todas las acciones realizadas.",
    proc6Title: "Cierre y verificación",
    proc6a: "Verificar la efectividad de las acciones correctivas antes de cerrar la queja.",
    proc6b: "Archivar todos los registros relacionados con la queja en el expediente correspondiente.",
    rec1: "Formato de Registro de Quejas", rec2: "Reportes de investigación", rec3: "Evidencias de acciones correctivas y preventivas",
    anx1: "Formato de Registro de Quejas", anx2: "Formato de acciones correctivas y preventivas", anx3: "Procedimiento de trazabilidad",
    exhibitTitle: "ANEXO A — REGISTRO DE QUEJAS DE PRODUCTO PRESENTADAS",
    exDateTime: "Fecha/hora de la queja", exCode: "Código", exBy: "Queja presentada por",
    exProduct: "Descripción del producto", exQty: "Cantidad de producto en cuestión", exNature: "Naturaleza de la queja",
    exMgmt: "Gerencia notificada", exFindings: "Hallazgos de la investigación",
    exActions: "Acciones a tomar para atender y corregir la queja y prevenir recurrencia",
    exResponse: "Fecha de respuesta a la queja", exSign: "Firma", exDate: "Fecha",
    emailSubject: "Programa de Recepción y Manejo de Quejas",
    emailBody: "Adjunto el Programa de Recepción y Manejo de Quejas para la auditoría ",
  },
};

/* Format a YYYY-MM-DD string to MM/DD/YYYY (US standard per project rules). */
function fmtDate(iso) {
  if (!iso) return "";
  var parts = String(iso).substring(0, 10).split("-");
  if (parts.length !== 3) return iso;
  return parts[1] + "/" + parts[2] + "/" + parts[0];
}

/* Default effective date = audit start date minus 5 days. Falls back to
   today − 5 days if the audit has no startDate. Returns YYYY-MM-DD. */
function defaultEffectiveDate(audit) {
  var base = (audit && audit.startDate) ? new Date(audit.startDate + "T00:00:00") : new Date();
  base.setDate(base.getDate() - 5);
  var y = base.getFullYear();
  var m = String(base.getMonth() + 1).padStart(2, "0");
  var d = String(base.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + d;
}

function buildHtml(data, audit, docLang, effDate, revision) {
  const t = T[docLang === "es" ? "es" : "en"];
  const company = (data && data.company) || {};
  const cert = (data && data.cert) || {};
  const certNum = cert.certNumber || company.organicCert || "";

  const li = function (txt) { return '<li>' + escHtml(txt) + '</li>'; };
  const subItem = function (txt) { return '<div class="subitem">○ ' + escHtml(txt) + '</div>'; };
  const section = function (title, body) {
    return '<div class="sec"><div class="sec-title">' + escHtml(title) + '</div>' + body + '</div>';
  };

  /* Repeating header for every sheet: logo + program band + meta table. */
  var headerBlock = ''
    + '<div class="doc-header">'
    + '  <div class="logo-wrap"><img src="' + LOGO_B64 + '" alt="Ground-Fresh International" /></div>'
    + '  <div class="company-name">Ground Fresh Internacional Inc</div>'
    + '</div>'
    + '<div class="program-band">' + escHtml(t.docHeaderProgram) + '</div>'
    + '<table class="meta-table"><tbody>'
    + '<tr>'
    + '<td><strong>' + escHtml(t.product) + ':</strong> ' + escHtml(t.productVal) + '</td>'
    + '<td><strong>' + escHtml(t.effFrom) + ':</strong> ' + escHtml(fmtDate(effDate)) + '</td>'
    + '<td><strong>' + escHtml(t.revision) + ':</strong> ' + escHtml(String(revision || "1")) + '</td>'
    + '</tr>'
    + '<tr><td colspan="3"><strong>' + escHtml(t.areaLabel) + ':</strong> ' + escHtml(t.areaVal)
    + (certNum ? '  ·  <strong>Organic Cert:</strong> ' + escHtml(certNum) : '')
    + '</td></tr>'
    + '</tbody></table>';

  var pageFoot = function (n) {
    return '<div class="page-foot">' + escHtml(t.pageOf) + ' ' + n + ' ' + escHtml(t.pageOfMid) + ' 3</div>';
  };

  /* Responsibilities. */
  var resp = ''
    + '<div class="role"><div class="role-name">' + escHtml(t.respQ) + '</div>'
    + subItem(t.respQ1) + subItem(t.respQ2) + subItem(t.respQ3) + subItem(t.respQ4) + '</div>'
    + '<div class="role"><div class="role-name">' + escHtml(t.respG) + '</div>'
    + subItem(t.respG1) + subItem(t.respG2) + '</div>'
    + '<div class="role"><div class="role-name">' + escHtml(t.respA) + '</div>'
    + subItem(t.respA1) + '</div>';

  var defs = '<ul>' + li(t.defComplaint) + li(t.defCorrective) + li(t.defPreventive) + li(t.defInvestigation) + li(t.defRecall) + '</ul>';

  var step = function (n, title, items) {
    var inner = items.map(function (x) { return '<li>' + escHtml(x) + '</li>'; }).join("");
    return '<div class="step"><div class="step-title">' + n + '. ' + escHtml(title) + '</div><ul>' + inner + '</ul></div>';
  };

  var records = '<ul>' + li(t.rec1) + li(t.rec2) + li(t.rec3) + '</ul>';

  /* Exhibit A — blank complaint record form. */
  var blankLine = '<div class="fill-line"></div>';
  var blankLines = function (n) { var s = ""; for (var i = 0; i < n; i++) s += blankLine; return s; };
  var fieldRow = function (label) {
    return '<div class="field"><span class="field-label">' + escHtml(label) + ':</span> ' + blankLine + '</div>';
  };
  var exhibit = ''
    + '<div class="exhibit-title">' + escHtml(t.exhibitTitle) + '</div>'
    + '<div class="field-2col">'
    + '<div class="field"><span class="field-label">' + escHtml(t.exDateTime) + ':</span> ' + blankLine + '</div>'
    + '<div class="field"><span class="field-label">' + escHtml(t.exCode) + ':</span> ' + blankLine + '</div>'
    + '</div>'
    + fieldRow(t.exBy)
    + fieldRow(t.exProduct)
    + fieldRow(t.exQty)
    + '<div class="field-block"><span class="field-label">' + escHtml(t.exNature) + ':</span>' + blankLines(4) + '</div>'
    + fieldRow(t.exMgmt)
    + fieldRow(t.exFindings)
    + '<div class="field-block"><span class="field-label">' + escHtml(t.exActions) + ':</span>' + blankLines(4) + '</div>'
    + fieldRow(t.exResponse)
    + '<div class="field-2col sign-row">'
    + '<div class="field"><span class="field-label">' + escHtml(t.exSign) + ':</span> ' + blankLine + '</div>'
    + '<div class="field"><span class="field-label">' + escHtml(t.exDate) + ':</span> ' + blankLine + '</div>'
    + '</div>'
    + '<div class="field-2col sign-row">'
    + '<div class="field"><span class="field-label">' + escHtml(t.exSign) + ':</span> ' + blankLine + '</div>'
    + '<div class="field"><span class="field-label">' + escHtml(t.exDate) + ':</span> ' + blankLine + '</div>'
    + '</div>';

  /* Sheet 1: header + procedure title + Purpose, Scope, Responsibilities, Definitions, Procedure 1-3 */
  var sheet1 = '<div class="sheet">' + headerBlock
    + '<div class="proc-title">' + escHtml(t.secProcTitle) + '</div>'
    + section(t.secPurpose, '<p>' + escHtml(t.purpose) + '</p>')
    + section(t.secScope, '<p>' + escHtml(t.scope) + '</p>')
    + section(t.secResp, resp)
    + section(t.secDef, defs)
    + '<div class="sec"><div class="sec-title">' + escHtml(t.secProc) + '</div>'
    + step(1, t.proc1Title, [t.proc1a, t.proc1b, t.proc1c])
    + step(2, t.proc2Title, [t.proc2a, t.proc2b])
    + step(3, t.proc3Title, [t.proc3a, t.proc3b, t.proc3c])
    + '</div>'
    + pageFoot(1) + '</div>';

  /* Sheet 2: header + Procedure 4-6 */
  var sheet2 = '<div class="sheet">' + headerBlock
    + '<div class="sec"><div class="sec-title">' + escHtml(t.secProc) + ' (cont.)</div>'
    + step(4, t.proc4Title, [t.proc4a, t.proc4b])
    + step(5, t.proc5Title, [t.proc5a, t.proc5b, t.proc5c])
    + step(6, t.proc6Title, [t.proc6a, t.proc6b])
    + '</div>'
    + pageFoot(2) + '</div>';

  /* Sheet 3: header + Records + Exhibit */
  var sheet3 = '<div class="sheet">' + headerBlock
    + section(t.secRecords, records)
    + section(t.secAnnex, exhibit)
    + pageFoot(3) + '</div>';

  var extraCss = ''
    + '.page{max-width:780px;margin:0 auto;padding:0}'
    + '.sheet{height:1056px;padding:20px 36px;page-break-after:always;position:relative;display:flex;flex-direction:column;overflow:hidden}'
    + '.sheet:last-child{page-break-after:auto}'
    + '.doc-header{display:flex;align-items:center;gap:14px;padding-bottom:8px;border-bottom:2px solid #5a7d3c;margin-bottom:4px}'
    + '.logo-wrap img{height:56px;width:auto;display:block}'
    + '.company-name{font-size:15px;font-weight:700;color:#1B3A4B}'
    + '.program-band{background:#dff0d0;border:1px solid #5a7d3c;text-align:center;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.04em;padding:4px;color:#1B3A4B;margin-bottom:0}'
    + '.meta-table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:12px}'
    + '.meta-table td{border:1px solid #5a7d3c;padding:3px 6px}'
    + '.proc-title{text-align:center;font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#1B3A4B;margin:0 0 14px}'
    + '.sec{margin:0 0 14px}'
    + '.sec-title{font-size:11.5px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#1B3A4B;border-bottom:2px solid #1B3A4B;padding-bottom:3px;margin-bottom:6px}'
    + '.sec p{font-size:11px;line-height:1.5;margin:0}'
    + '.sec ul{margin:0;padding-left:18px}'
    + '.sec li{font-size:11px;line-height:1.5;margin-bottom:5px}'
    + '.role{margin-bottom:8px}'
    + '.role-name{font-size:11px;font-weight:700;color:#333;margin-bottom:2px}'
    + '.subitem{font-size:11px;line-height:1.5;padding-left:24px;color:#444}'
    + '.step{margin-bottom:9px}'
    + '.step-title{font-size:11px;font-weight:700;color:#1B3A4B;margin-bottom:2px}'
    + '.step ul{margin:0;padding-left:18px}'
    + '.step li{font-size:11px;line-height:1.5;margin-bottom:3px}'
    + '.exhibit-title{text-align:center;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:#1B3A4B;margin:6px 0 16px}'
    + '.field{margin-bottom:11px;font-size:11px;display:flex;align-items:flex-end;gap:6px}'
    + '.field-label{font-weight:700;color:#333;white-space:nowrap}'
    + '.field-2col{display:flex;gap:18px}'
    + '.field-2col .field{flex:1}'
    + '.field-block{margin-bottom:11px;font-size:11px}'
    + '.field-block .field-label{display:block;margin-bottom:8px}'
    + '.fill-line{flex:1;border-bottom:1px solid #333;min-height:16px;min-width:60px}'
    + '.field-block .fill-line{margin-bottom:10px}'
    + '.sign-row{margin-top:6px}'
    + '.page-foot{margin-top:auto;padding-top:10px;text-align:center;font-size:9px;color:#888}';

  return '<!doctype html><html><head><meta charset="utf-8"><style>' + reportStyles() + extraCss + '</style>' +
    '</head><body><div class="page">' +
    sheet1 + sheet2 + sheet3 +
    '</div></body></html>';
}

export default function ComplaintsProcedurePanel({ lang, audit, onClose, onLogReport, initialParams }) {
  const t = T[lang === "es" ? "es" : "en"];
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [docLang, setDocLang] = useState(lang === "es" ? "es" : "en");
  const [effDate, setEffDate] = useState(defaultEffectiveDate(audit));
  const [revision, setRevision] = useState("1");
  const lastBlobRef = useRef(null);
  const lastFilenameRef = useRef("");

  useEffect(() => {
    let alive = true;
    Promise.all([
      loadFromSupabase("company_profile").catch(function () { return []; }),
      loadFromSupabase("certifications").catch(function () { return []; }),
    ]).then(function (res) {
      if (!alive) return;
      var company = (res[0] || []).find(function (r) { return String(r.id) === "gfi"; }) || (res[0] || [])[0] || {};
      var cert = (res[1] || []).find(function (r) { return r.entityType === "gfi" && r.certType === "organic"; }) || {};
      setData({ company: company, cert: cert });
    });
    return function () { alive = false; };
  }, []);

  const makeHtml = useCallback(
    () => buildHtml(data || { company: {}, cert: {} }, audit, docLang, effDate, revision),
    [data, audit, docLang, effDate, revision]
  );
  const previewHtml = useMemo(() => makeHtml(), [makeHtml]);

  const handlePreview = () => openPreviewWindow(makeHtml());
  const fileBase = "Complaint_Program_" + (audit && audit.name || "");
  const handlePdf = async () => {
    setBusy(true);
    try {
      const blob = await htmlToPdfBlob(makeHtml(), { orientation: "portrait" });
      const fn = safeFilename(fileBase) + ".pdf";
      lastBlobRef.current = blob; lastFilenameRef.current = fn;
      downloadBlob(blob, fn);
      if (onLogReport) onLogReport({ type: "complaints_proc", title: (lang === "es" ? "PDF descargado" : "PDF downloaded") + " · " + fn, params: { docLang: docLang, effDate: effDate, revision: revision }, blob: blob, filename: fn, mime: "application/pdf" });
    } finally { setBusy(false); }
  };

  const profile = (data && data.company) || {};
  const signatures = (profile && profile.emailSignatures) || [];
  const fromEmail = (profile && (profile.outboundEmail || profile.email)) || "";
  /* 2026-09-30 — excludes Sweetleaf/automated accounts, same fix as
     OrganicAuditRegistry.js's own emailAccounts. */
  const emailAccounts = ((profile && profile.emailAccounts) || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });
  const et = T[docLang === "es" ? "es" : "en"];

  const btnSecondary = { padding: "7px 14px", border: "1px solid var(--color-border)", borderRadius: 6, background: "#fff", fontSize: 12, cursor: "pointer" };
  const btnPrimary = { padding: "7px 14px", border: "none", borderRadius: 6, background: "#1a2636", color: "#fff", fontSize: 12, cursor: "pointer" };
  const ctrlWrap = { display: "flex", flexDirection: "column", gap: 2 };
  const ctrlLabel = { fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 };
  const ctrlInput = { padding: "5px 8px", border: "1px solid var(--color-border)", borderRadius: 6, fontSize: 12, background: "#fff" };

  return (
    <DraggableModal title={t.title + " · " + (audit && audit.name || "")} onClose={onClose} zIndex={10003}>
      {/* Controls: document language, effective date, revision */}
      <div style={{ padding: "12px 24px 0", display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={ctrlWrap}>
          <span style={ctrlLabel}>{t.langLabel}</span>
          <select value={docLang} onChange={e => setDocLang(e.target.value)} style={ctrlInput}>
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>
        <div style={ctrlWrap}>
          <span style={ctrlLabel}>{t.effDateLabel}</span>
          <input type="date" value={effDate} onChange={e => setEffDate(e.target.value)} style={ctrlInput} />
        </div>
        <div style={ctrlWrap}>
          <span style={ctrlLabel}>{t.revLabel}</span>
          <input value={revision} onChange={e => setRevision(e.target.value)} style={{ ...ctrlInput, width: 60, fontFamily: "'JetBrains Mono', monospace" }} />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "14px 24px 20px" }}>
        <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", background: "#fff", height: 460 }}>
          <iframe title="complaints-preview" srcDoc={previewHtml} style={{ width: "100%", height: "100%", border: 0 }} />
        </div>
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{busy ? t.busy : ""}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={btnSecondary}>{t.close}</button>
          <button onClick={handlePreview} style={btnSecondary}>{t.preview}</button>
          <button onClick={() => setShowEmail(true)} style={btnSecondary}>{t.email}</button>
          <button onClick={handlePdf} style={btnPrimary} disabled={busy}>{t.pdf}</button>
        </div>
      </div>

      {showEmail && (
        <EmailDialog
          lang={lang}
          title={t.email}
          recipients={(audit && audit.auditorEmail) ? [{ name: audit.auditorName || "", email: audit.auditorEmail, checked: true }] : []}
          docOptions={[{ key: "report", label: et.title, checked: true }]}
          defaultSubject={et.emailSubject + " — " + (audit && audit.name || "")}
          defaultBody={et.emailBody + (audit && audit.name || "") + "."}
          fromEmail={fromEmail}
          emailAccounts={emailAccounts}
          signatures={signatures}
          postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, profile, null)}
          buildAttachments={async () => {
            const blob = await htmlToPdfBlob(makeHtml(), { orientation: "portrait" });
            const fn = safeFilename(fileBase) + ".pdf";
            lastBlobRef.current = blob; lastFilenameRef.current = fn;
            return [{ filename: fn, blob: blob, mimeType: "application/pdf" }];
          }}
          onSent={() => { setShowEmail(false); if (onLogReport) onLogReport({ type: "complaints_proc", title: (lang === "es" ? "Enviado por correo" : "Sent by email"), params: { docLang: docLang, effDate: effDate, revision: revision }, blob: lastBlobRef.current, filename: lastFilenameRef.current, mime: "application/pdf" }); }}
          onClose={() => setShowEmail(false)}
        />
      )}
    </DraggableModal>
  );
}
